"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Elements } from "@stripe/react-stripe-js"
import { PayPalScriptProvider } from "@paypal/react-paypal-js"
import { Lock } from "lucide-react"

import { cn } from "@/lib/utils"
import { situationQueryFromUrl } from "@/lib/analytics"
import { entryPageFromReferrer } from "@/lib/leads/entry-attribution"
import { readAcquisitionContext } from "@/lib/acquisition/context"
import { Input } from "@/components/ui/input"
import { MarketingButton, MarketingLink, marketingActionClass } from "@/components/marketing/marketing-action"
import { EmailLink } from "@/components/marketing/email-link"
import {
  ELEMENTS_APPEARANCE,
  PAYPAL_CLIENT_ID,
  deferredChargeLine,
  formatAmount,
  isLikelyEmail,
  reloadStripe,
  renewsQueryFromUrl,
  reportPaymentFormLoadFailure,
  stripeLoader,
  stripePromise,
  weekendQueryFromUrl,
  type DeferredStart,
  type OfferCopy,
  type PayPalStandaloneOffer,
  type PaymentFormLoadStage,
  type PromoProps,
  type SessionState,
} from "./checkout-shared"

/** The `deferredStart` the checkout route answers with, when it is well formed. */
function deferredStartFrom(value: unknown): DeferredStart | null {
  if (!value || typeof value !== "object") return null
  const { firstChargeAt, recurringAmount } = value as Record<string, unknown>
  return typeof firstChargeAt === "string" && typeof recurringAmount === "number"
    ? { firstChargeAt, recurringAmount }
    : null
}
import { CheckoutFallback, checkoutIsDown } from "./checkout-fallback"
import { OrderSummary } from "./order-summary"
import {
  PayPalOnlyPanel,
  PayPalResultNotice,
  PaymentPanel,
  type PayPalResult,
  type PayPalStartFailure,
} from "./payment-methods"

// The client half of the /pay/embedded checkout (the server half is
// ../page.tsx, which resolves the plan's copy and amount BEFORE any network
// round-trip so the confirmatory header and order summary paint immediately —
// the owner's "payment pages need to all load faster"). Everything that talks
// to Stripe or PayPal lives down here; nothing about the confirm/consent
// mechanics changed in the 2026-08-20 restructure — only the order, the
// chrome, and the copy.

const SECTION = "mx-auto max-w-[92rem] px-5 sm:px-8 lg:px-10"

export function NoticeShell({
  heading,
  body,
  children,
}: {
  heading: string
  body: string
  children?: React.ReactNode
}) {
  return (
    <section className="tone-paper">
      <div
        className={cn(
          SECTION,
          "flex min-h-[50vh] flex-col items-center justify-center gap-3 py-16 text-center",
        )}
      >
        <h1 className="serious-display text-[clamp(1.6rem,3vw,2.2rem)] text-[var(--color-ink)]">
          {heading}
        </h1>
        <p className="max-w-[36ch] text-[0.95rem] leading-[1.6] text-[var(--color-ink-muted)]">
          {body}
        </p>
        {children}
      </div>
    </section>
  )
}

/** The contact line under every checkout error, so a buyer who cannot get
 *  through always has a person to reach. */
function CheckoutContactLine() {
  return (
    <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
      Still not working? Email <EmailLink />.
    </p>
  )
}

/** The checkout could not open. His answer: "Checkout error: retry +
 *  contact", so this is a way forward instead of one red line.
 *
 *  Try again re-runs the exact first call the page load made. That call
 *  failed without handing the page a PaymentIntent, so there is nothing to
 *  reuse: a retry opens at most one PaymentIntent, the same as a reload, and
 *  the button is gone (the page is back to loading) until that call answers.
 *  A 401 gets a sign-in link that brings him straight back to this checkout. */
function CheckoutErrorNotice({
  message,
  needsSignIn,
  onRetry,
}: {
  message: string
  needsSignIn: boolean
  onRetry: () => void
}) {
  // Rendered only after the first call answers, so always in the browser.
  const here =
    typeof window === "undefined" ? "/pay/embedded" : `${window.location.pathname}${window.location.search}`
  return (
    <div className="mx-auto flex max-w-[40rem] flex-col items-center gap-4 text-center">
      <p role="alert" className="text-[0.95rem] text-[var(--color-vermilion)]">
        {message}
      </p>
      {needsSignIn ? (
        <MarketingLink href={`/sign-in?callbackUrl=${encodeURIComponent(here)}`} variant="accent" size="lg">
          Sign in
        </MarketingLink>
      ) : (
        <MarketingButton type="button" onClick={onRetry} variant="accent" size="lg">
          Try again
        </MarketingButton>
      )}
      <CheckoutContactLine />
    </div>
  )
}

/** The centered checkout intro, after the owner's final subtraction pass
 *  (2026-08-20): the heading and the one-line what-it-is, nothing else. The
 *  secure-checkout marker lives in the masthead alone, and the price lives
 *  in the order card and on the pay button — three repetitions of $3,000
 *  was two too many. */
/** The security line under the heading names whoever holds the payment
 *  details on THIS screen, his answer: "it depends on the page being shown
 *  remember that we may restore stripe". Stripe is the primary checkout and
 *  keeps its line beside its own form; PayPal's line is for the PayPal-only
 *  backup alone. */
const SECURITY_LINES = {
  stripe: "Your card details go straight to Stripe. We never see or store them.",
  paypal: "Your payment details go straight to PayPal. We never see or store them.",
} as const

function CheckoutIntro({
  offer,
  securityLine,
}: {
  offer: OfferCopy | null
  /** "stripe" beside Stripe's form, "paypal" beside the PayPal-only panel,
   *  "none" where neither form is on the screen (a load failure, the email
   *  step). "reserved" keeps the line's height, unread, while the form is
   *  still on its way (the pre-paint), so the page does not jump when it
   *  arrives and never names a processor before the form that uses it. */
  securityLine: keyof typeof SECURITY_LINES | "reserved" | "none"
}) {
  const heading = offer?.confirmHeading ?? (offer ? `You're buying ${offer.title}.` : "Secure checkout")
  // No product subtitle here, his ruling: "you don't need the subtitle
  // describing the product on the payment pages the checkmarks already
  // handle that." The security line is the only sub-line.
  return (
    <div className="mx-auto max-w-[52rem] pb-7 text-center md:pb-8">
      <h1 className="serious-display mx-auto max-w-[24ch] text-balance text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.02] text-[var(--color-ink)]">
        {heading}
      </h1>
      {securityLine !== "none" && (
        <p
          aria-hidden={securityLine === "reserved" ? true : undefined}
          className={cn(
            "mx-auto mt-2.5 max-w-[52ch] text-[0.96rem] leading-[1.55] text-[var(--color-ink-muted)]",
            securityLine === "reserved" && "invisible",
          )}
        >
          {SECURITY_LINES[securityLine === "reserved" ? "stripe" : securityLine]}
        </p>
      )}
    </div>
  )
}

/** The one payment form, once a PaymentIntent exists. Exported so
 *  /community/join mounts this exact form for the membership rather than a
 *  second look (his order: "I just want all of them to look the exact same"). */
export function ReadyCheckout({
  clientSecret,
  paymentIntentId,
  offer,
  amountCents: initialAmountCents,
  currency,
  prefillEmail,
  paypalReady: paypalConfigured,
  recurringInterval,
  promo,
  intentKind = "payment",
  deferredStart = null,
}: {
  clientSecret: string
  paymentIntentId: string
  offer: OfferCopy
  amountCents: number
  currency: string
  prefillEmail: string | null
  paypalReady: boolean
  recurringInterval: string | null
  /** The typed hand-out-code affordance ("Have a promo code?"), null on a
   *  recurring tier — a percent-off cannot reach a subscription's later
   *  invoices, so the box never renders there. */
  promo: PromoProps | null
  /** "setup" when nothing is charged today (a weekly plan that starts when
   *  his prepaid block ends): the form confirms a SetupIntent. */
  intentKind?: "payment" | "setup"
  deferredStart?: DeferredStart | null
}) {
  // PayPal is off for a recurring tier. Our PayPal path captures a ONE-OFF
  // order against the PaymentIntent amount (/api/paypal/create-order), which
  // pays week one and leaves the subscription with no payment method for week
  // two. Adding PayPal subscriptions is a separate build, not a flag.
  const paypalReady = paypalConfigured && !recurringInterval
  // No agreement checkbox. His words: "no need for the extra friction just
  // have the terms at the bottom and say that by paying they agree to xyz
  // terms". Paying is the agreement; the line sits at the foot of the form.
  const agreed = true
  const amountCents = initialAmountCents
  const [paypalResult, setPaypalResult] = useState<PayPalResult | null>(null)

  // The payment form can fail to load: a content blocker or network filter
  // stops Stripe's script, or the script arrives and the Payment Element
  // reports a load error. Left alone, that showed an empty Payment heading
  // above a Pay button that could never work, with no word to the buyer and
  // none to the owner. Now the buyer is told and can retry, and the owner is
  // alerted once for this intent.
  // Seeded from the shared current loader, so a checkout remounted after a
  // successful retry starts from the attempt that worked.
  const [loader, setLoader] = useState(() => stripeLoader())
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [loadFailure, setLoadFailure] = useState<{ stage: PaymentFormLoadStage; detail: string } | null>(null)
  const loadFailureReported = useRef(false)

  const failLoad = useCallback((stage: PaymentFormLoadStage, detail: string) => {
    setLoadFailure((current) => current ?? { stage, detail })
  }, [])

  useEffect(() => {
    if (!loader) return
    let live = true
    loader.catch((err: unknown) => {
      if (live) failLoad("stripe-js", err instanceof Error ? err.message : String(err))
    })
    return () => {
      live = false
    }
  }, [loader, failLoad])

  useEffect(() => {
    if (!loadFailure || loadFailureReported.current) return
    loadFailureReported.current = true
    void reportPaymentFormLoadFailure({
      paymentIntentId,
      clientSecret,
      stage: loadFailure.stage,
      detail: loadFailure.detail,
    })
  }, [loadFailure, paymentIntentId, clientSecret])

  // Try again reloads the form against this SAME intent (a fresh Elements
  // instance, and a fresh copy of Stripe's script when that was what failed);
  // it never opens a second PaymentIntent. Only the payment panel remounts,
  // and the email he typed lives here, so nothing he entered is lost.
  const retryLoad = useCallback(() => {
    if (loadFailure?.stage === "stripe-js") setLoader(reloadStripe())
    setLoadFailure(null)
    setLoadAttempt((n) => n + 1)
  }, [loadFailure])
  const [email, setEmail] = useState(prefillEmail ?? "")

  if (!loader) return null

  if (paypalResult) {
    return <PayPalResultNotice result={paypalResult} />
  }

  const canPay = agreed

  const checkout = (
    <>
      {/* When the payment form failed to load, its notice replaces the Stripe
          form, so the line about Stripe's form goes with it. */}
      <CheckoutIntro offer={offer} securityLine={loadFailure ? "none" : "stripe"} />
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <OrderSummary
          offer={offer}
          amountCents={amountCents}
          currency={currency}
          recurringInterval={recurringInterval}
          promo={promo}
          deferredStart={deferredStart}
        />
        {/* Elements renders no element of its own, so the panel is still the
            grid's second column. It wraps the panel alone so Try again
            remounts only what depends on Stripe. */}
        <Elements key={loadAttempt} stripe={loader} options={{ clientSecret, appearance: ELEMENTS_APPEARANCE }}>
          <PaymentPanel
            paymentIntentId={paymentIntentId}
            clientSecret={clientSecret}
            amountCents={amountCents}
            currency={currency}
            agreed={canPay}
            email={email}
            onEmailChange={setEmail}
            paypalReady={paypalReady}
            onPaypalSuccess={setPaypalResult}
            recurringInterval={recurringInterval}
            offer={offer}
            intentKind={intentKind}
            deferredStart={deferredStart}
            onPaymentElementLoadError={(message) => failLoad("payment-element", message)}
            loadFailureNotice={
              loadFailure ? (
                <CheckoutErrorNotice
                  message="The payment form didn't load. A weak connection or an ad blocker can stop it."
                  needsSignIn={false}
                  onRetry={retryLoad}
                />
              ) : null
            }
          />
        </Elements>
      </div>
    </>
  )

  if (!PAYPAL_CLIENT_ID || !paypalReady) return checkout

  // PayPal Pay Later rides inside this same button group, his answer "PayPal
  // Pay Later + Elective + ask Affirm". enable-funding is PayPal's documented
  // way to make sure an eligible funding source is rendered rather than left to
  // its default; PayPal paints the button and every word on it, and a Pay Later
  // order is the same CAPTURE order the PayPal button creates, so create,
  // capture and the webhook-only fulfilment are unchanged.
  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        currency: currency.toUpperCase(),
        intent: "capture",
        enableFunding: "paylater",
      }}
    >
      {checkout}
    </PayPalScriptProvider>
  )
}

/** Whether a failed first checkout call means Stripe cannot take this payment
 *  right now: a server error or no answer at all. A sign-in request, a
 *  deliberate refusal, a bad request and the rate limit are answers about this
 *  buyer or this plan, so PayPal does not step in for them. */
function stripeCannotTakePayment(httpStatus: number | undefined): boolean {
  return httpStatus === undefined || httpStatus >= 500
}

/** The checkout when Stripe cannot take payments and PayPal stands alone (his
 *  answer on how buyers pay: "Bank transfer + PayPal", PayPal staying the
 *  normal card checkout). The order summary is the server-resolved one the
 *  page already painted; the price itself is computed again on the server by
 *  /api/paypal/create-order from the plan catalog, never sent from here.
 *  PayPal's button group, Pay Later and its card button inside it, is the one
 *  way to pay, so the security line names PayPal, not Stripe. */
function PayPalOnlyCheckout({
  planId,
  offer,
  amountCents,
  currency,
  onStartFailed,
}: {
  planId: string
  offer: OfferCopy
  amountCents: number
  currency: string
  onStartFailed: (failure: PayPalStartFailure) => void
}) {
  const [paypalResult, setPaypalResult] = useState<PayPalResult | null>(null)
  const request = useCallback(
    () => ({
      // A prepaid block bought from its renewal reminder, and a Group Bootcamp
      // weekend, carry exactly as they do on the Stripe-backed checkout.
      renews: renewsQueryFromUrl(),
      weekend: weekendQueryFromUrl(),
      acquisition_context: readAcquisitionContext() ?? undefined,
    }),
    [],
  )

  if (!PAYPAL_CLIENT_ID) return null
  if (paypalResult) return <PayPalResultNotice result={paypalResult} />

  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        currency: currency.toUpperCase(),
        intent: "capture",
        enableFunding: "paylater",
      }}
    >
      <CheckoutIntro offer={offer} securityLine="paypal" />
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <OrderSummary offer={offer} amountCents={amountCents} currency={currency} recurringInterval={null} />
        <PayPalOnlyPanel
          planId={planId}
          request={request}
          amountCents={amountCents}
          currency={currency}
          onSuccess={setPaypalResult}
          onStartFailed={onStartFailed}
        />
      </div>
    </PayPalScriptProvider>
  )
}

/** What the buyer sees the instant the page paints, before the PaymentIntent
 *  exists: the real order summary from the server-resolved copy, and a quiet
 *  note where the payment form is about to appear. This replaces the old
 *  full-page "Setting up your checkout…" line, which left a $3,000 buyer
 *  staring at nothing (owner: "the payment pages need to all load faster"). */
function PrePaintSummary({
  offer,
  amountCents,
  currency,
}: {
  offer: OfferCopy
  amountCents: number | null
  currency: string
}) {
  return (
    <>
      <CheckoutIntro offer={offer} securityLine="reserved" />
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
      <OrderSummary
        offer={offer}
        amountCents={amountCents}
        currency={currency}
        recurringInterval={null}
      />
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-2 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 text-center md:p-7">
        <Lock aria-hidden size={16} className="text-[var(--color-vermilion)]" />
        <p className="text-[0.92rem] text-[var(--color-ink-muted)]">Preparing secure payment…</p>
      </div>
      </div>
    </>
  )
}

/** The email-first step a RECURRING tier shows before its Stripe subscription
 * exists. Deliberately spare: the order summary the buyer already came here to
 * read, one field, one button. It is not a marketing surface and it must not
 * become one, because every element between him and the payment form is
 * friction on a page he arrived at ready to pay. */
function RecurringEmailStep({
  offer,
  amountCents,
  currency,
  recurringInterval,
  prefillEmail,
  submitting,
  error,
  onSubmit,
  deferredStart,
  createsAccount,
}: {
  offer: OfferCopy
  amountCents: number
  currency: string
  recurringInterval: string | null
  prefillEmail: string | null
  submitting: boolean
  error: string
  onSubmit: (email: string) => void
  /** A weekly plan that starts when his prepaid block ends: $0 today. */
  deferredStart: DeferredStart | null
  /** His account is made from this address once he pays (the article
   *  subscription), so the hint names the account even on a recurring plan. */
  createsAccount: boolean
}) {
  const [email, setEmail] = useState(prefillEmail ?? "")
  const emailValid = isLikelyEmail(email)

  return (
    <form
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        if (!emailValid || submitting) return
        onSubmit(email.trim())
      }}
      className="mx-auto flex max-w-[34rem] flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 md:p-7"
    >
      <div>
        <h2 className="serious-display text-[clamp(1.4rem,2.4vw,1.8rem)] leading-tight text-[var(--color-ink)]">
          {offer.title}
        </h2>
      </div>

      <div className="flex items-baseline justify-between border-y border-[var(--color-rule)] py-4">
        <span className="text-[0.85rem] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-soft)]">
          Due today
        </span>
        <span className="serious-display text-[1.4rem] text-[var(--color-ink)]">
          {recurringInterval && !deferredStart
            ? `${formatAmount(amountCents, currency)} ${recurringInterval}`
            : formatAmount(amountCents, currency)}
        </span>
      </div>
      {deferredStart && (
        <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
          {deferredChargeLine(formatAmount(deferredStart.recurringAmount, currency), deferredStart.firstChargeAt)}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <label htmlFor="recurring-email" className="text-[0.82rem] font-semibold text-[var(--color-ink)]">
          Your email
        </label>
        <Input
          id="recurring-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          aria-invalid={email.length > 0 && !emailValid}
        />
        <p className="text-[0.82rem] leading-[1.4] text-[var(--color-ink-muted)]">
          {recurringInterval && !createsAccount
            ? "This is where your receipts go, and it is the address you email if you want to stop."
            : "Your receipt and your account go to this email. We send you a link to set your password."}
        </p>
      </div>

      {error && (
        <div className="flex flex-col gap-1">
          <p role="alert" className="text-[0.88rem] leading-[1.5] text-[var(--color-vermilion)]">
            {error}
          </p>
          <CheckoutContactLine />
        </div>
      )}

      <button
        type="submit"
        disabled={!emailValid || submitting}
        className={cn(marketingActionClass({ variant: "accent", size: "lg" }), "w-full justify-center")}
      >
        {submitting ? "Setting up…" : "Continue to payment"}
      </button>
    </form>
  )
}

/** Owns the async create-payment-intent call. */
function EmbeddedCheckoutSession({
  plan,
  price,
  amount,
  initialOffer,
  initialAmountCents,
  paypalStandalone,
}: {
  plan: string | null
  price: string | null
  amount: string | null
  initialOffer: OfferCopy | null
  initialAmountCents: number | null
  paypalStandalone: PayPalStandaloneOffer | null
}) {
  const [state, setState] = useState<SessionState>({ status: "loading" })
  // Bumped by the error notice's Try again, which re-runs the first call below.
  const [attempt, setAttempt] = useState(0)
  const [paypalReadiness, setPaypalReadiness] = useState<"checking" | "ready" | "unavailable">(
    PAYPAL_CLIENT_ID ? "checking" : "unavailable",
  )
  const paypalReady = paypalReadiness === "ready"
  // Set when PayPal, standing in for Stripe, could not start either (its script
  // did not load, or create-order could not open an order): the buyer then gets
  // the send-us-your-details fallback, as before PayPal stood alone.
  const [paypalStandaloneFailed, setPaypalStandaloneFailed] = useState(false)
  const onPayPalStandaloneStartFailed = useCallback((failure: PayPalStartFailure) => {
    // A sign-in answer keeps its sign-in button and gets no fallback; anything
    // else means PayPal cannot start either.
    if (failure.status === 401) {
      setState({ status: "error", message: failure.message, needsSignIn: true, httpStatus: 401 })
      return
    }
    setPaypalStandaloneFailed(true)
  }, [])
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [emailError, setEmailError] = useState("")
  const [emailErrorStatus, setEmailErrorStatus] = useState<number | undefined>(undefined)
  // Lives here, not in the panel, so Try again (which unmounts the error
  // branch) cannot wipe the "we have your details" confirmation.
  const [fallbackSent, setFallbackSent] = useState(false)
  // The typed hand-out code ("Have a promo code?" box — see OrderSummary).
  // Applying it re-prices by creating a FRESH PaymentIntent with the code
  // included; it never touches the initial fast-paint creation below, which
  // is deliberately code-less so the checkout still loads instantly for the
  // buyer who has nothing to type.
  const [promoApplying, setPromoApplying] = useState(false)
  const [promoError, setPromoError] = useState("")
  const [promoApplied, setPromoApplied] = useState(false)
  const buyerEmail = state.status === "ready" ? state.prefillEmail : null

  const applyPromoCode = useCallback(
    async (rawCode: string) => {
      setPromoApplying(true)
      setPromoError("")
      try {
        const payload = plan ? { planId: plan } : price ? { price } : { amount }
        const res = await fetch("/api/stripe/embedded-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            // A signed-out course buyer's email, or the route answers
            // requiresEmail with no PaymentIntent and the code never applies.
            // The route ignores it for a signed-in buyer.
            ...(buyerEmail ? { email: buyerEmail } : {}),
            code: rawCode,
            originPath: entryPageFromReferrer(),
            acquisition_context: readAcquisitionContext() ?? undefined,
            situation: situationQueryFromUrl(),
            renews: renewsQueryFromUrl(),
            // The chosen Group Bootcamp weekend survives a code re-price.
            weekend: weekendQueryFromUrl(),
          }),
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.clientSecret) {
          setPromoError(data?.error || "We couldn't apply that code. Please try again.")
          return
        }
        if (!data.codeApplied) {
          setPromoError(data.codeError || "That code isn't valid.")
          return
        }
        setState({
          status: "ready",
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
          intentKind: data.intentKind === "setup" ? "setup" : "payment",
          deferredStart: deferredStartFrom(data.deferredStart),
          offer: data.offer,
          amountCents: data.amount,
          currency: data.currency,
          prefillEmail: data.email ?? null,
          recurringInterval: data.recurringInterval ?? null,
          guestCheckout: Boolean(data.guestCheckout),
        })
        setPromoApplied(true)
      } catch {
        setPromoError("We couldn't apply that code. Please try again.")
      } finally {
        setPromoApplying(false)
      }
    },
    [plan, price, amount, buyerEmail],
  )

  /** Phase two of the recurring flow: the same endpoint, now with the address,
   * which is what actually opens the Stripe subscription. A recurring tier
   * never carries a hand-out code — a percent-off on a PaymentIntent cannot
   * reach a subscription's later invoices — so no code field belongs here. */
  const submitRecurringEmail = useCallback(
    async (email: string) => {
      if (!plan && !price && !amount) return
      // The same entry the page opened with: a plan, or a price (a signed-out
      // course buyer's payment-retry link opens on its price).
      const payload = plan ? { planId: plan } : price ? { price } : { amount }
      setEmailSubmitting(true)
      setEmailError("")
      try {
        const res = await fetch("/api/stripe/embedded-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            email,
            originPath: entryPageFromReferrer(),
            // This visit's acquisition envelope, so the PaymentIntent can carry
            // its id and the cleared sale can be joined back to the page that
            // produced it. See web/lib/acquisition/context.ts.
            acquisition_context: readAcquisitionContext() ?? undefined,
            situation: situationQueryFromUrl(),
            // The weekly link in a prepaid block's renewal reminder names the
            // ending block, so the first weekly charge waits for its end.
            renews: renewsQueryFromUrl(),
          }),
        })
        const data = await res.json().catch(() => null)
        if (!res.ok || !data?.clientSecret) {
          setEmailError(data?.error || "We couldn't start that checkout. Please try again.")
          setEmailErrorStatus(res.status)
          return
        }
        setState({
          status: "ready",
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
          intentKind: data.intentKind === "setup" ? "setup" : "payment",
          deferredStart: deferredStartFrom(data.deferredStart),
          offer: data.offer,
          amountCents: data.amount,
          currency: data.currency,
          prefillEmail: data.email ?? email,
          recurringInterval: data.recurringInterval ?? null,
          guestCheckout: Boolean(data.guestCheckout),
        })
      } catch {
        setEmailError("We couldn't start that checkout. Please try again.")
        setEmailErrorStatus(undefined)
      } finally {
        setEmailSubmitting(false)
      }
    },
    [plan, price, amount],
  )

  useEffect(() => {
    let cancelled = false

    if (PAYPAL_CLIENT_ID) {
      void fetch("/api/paypal/readiness", { cache: "no-store" })
        .then(async (res) => {
          const data = await res.json().catch(() => null)
          if (!cancelled) setPaypalReadiness(res.ok && data?.ready === true ? "ready" : "unavailable")
        })
        .catch(() => {
          if (!cancelled) setPaypalReadiness("unavailable")
        })
    }

    async function run() {
      const payload = plan ? { planId: plan } : price ? { price } : { amount }
      try {
        const res = await fetch("/api/stripe/embedded-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            originPath: entryPageFromReferrer(),
            // This visit's acquisition envelope, so the PaymentIntent can carry
            // its id and the cleared sale can be joined back to the page that
            // produced it. See web/lib/acquisition/context.ts.
            acquisition_context: readAcquisitionContext() ?? undefined,
            situation: situationQueryFromUrl(),
            // A prepaid block bought from its renewal reminder names the block
            // that is ending, so the new one starts the day that one ends.
            renews: renewsQueryFromUrl(),
            // A Group Bootcamp opened from "Take this weekend" names that
            // weekend; the route stamps it only when it is still published.
            weekend: weekendQueryFromUrl(),
          }),
        })
        const data = await res.json().catch(() => null)
        if (cancelled) return

        // A RECURRING tier answers the first call with the offer and no Stripe
        // object, because a subscription cannot exist before we have an email.
        if (res.ok && data?.requiresEmail) {
          setState({
            status: "needs-email",
            offer: data.offer,
            amountCents: data.amount,
            currency: data.currency,
            recurringInterval: data.recurringInterval ?? null,
            prefillEmail: data.email ?? null,
            deferredStart: deferredStartFrom(data.deferredStart),
            createsAccount: data.createsAccount === true,
          })
          return
        }

        if (!res.ok || !data?.clientSecret) {
          const message =
            res.status === 401
              ? "Please sign in first so we can add this to your account after payment."
              : data?.error || "Checkout is unavailable. Please try again."
          setState({ status: "error", message, needsSignIn: res.status === 401, httpStatus: res.status })
          return
        }

        setState({
          status: "ready",
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
          intentKind: data.intentKind === "setup" ? "setup" : "payment",
          deferredStart: deferredStartFrom(data.deferredStart),
          offer: data.offer,
          amountCents: data.amount,
          currency: data.currency,
          prefillEmail: data.email ?? null,
          recurringInterval: data.recurringInterval ?? null,
          guestCheckout: Boolean(data.guestCheckout),
        })

      } catch {
        if (!cancelled) {
          setState({ status: "error", message: "Checkout is unavailable. Please try again." })
        }
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [plan, price, amount, attempt])

  if (state.status === "loading") {
    // With a known plan the real summary paints instantly; the generic line
    // survives only for ?price= / ?amount= entries, whose copy the server
    // cannot know.
    if (initialOffer) {
      return <PrePaintSummary offer={initialOffer} amountCents={initialAmountCents} currency="usd" />
    }
    return (
      <p className="text-center text-[0.95rem] text-[var(--color-ink-muted)]">
        Setting up your checkout…
      </p>
    )
  }

  if (state.status === "error") {
    // Stripe cannot take this payment, so PayPal stands alone for a one-time
    // plan the server priced from the catalog (paypalStandalone). A recurring
    // or retired plan, a price or amount link, and anything PayPal could not
    // start keep the fallback below.
    if (
      paypalStandalone &&
      stripeCannotTakePayment(state.httpStatus) &&
      !paypalStandaloneFailed &&
      paypalReadiness !== "unavailable"
    ) {
      if (paypalReadiness === "checking") {
        return (
          <PrePaintSummary
            offer={paypalStandalone.offer}
            amountCents={paypalStandalone.amountCents}
            currency={paypalStandalone.currency}
          />
        )
      }
      return (
        <PayPalOnlyCheckout
          planId={paypalStandalone.planId}
          offer={paypalStandalone.offer}
          amountCents={paypalStandalone.amountCents}
          currency={paypalStandalone.currency}
          onStartFailed={onPayPalStandaloneStartFailed}
        />
      )
    }

    return (
      <div className="flex flex-col gap-8">
        <CheckoutErrorNotice
          message={state.message}
          needsSignIn={Boolean(state.needsSignIn)}
          onRetry={() => {
            setPaypalStandaloneFailed(false)
            setState({ status: "loading" })
            setAttempt((n) => n + 1)
          }}
        />
        {/* Checkout could not open, so the buyer is never left at a dead end.
            A 401 is a sign-in problem, not an outage, so it keeps its button. */}
        {checkoutIsDown(state.httpStatus) ? (
          <CheckoutFallback
            offerTitle={initialOffer?.title ?? null}
            plan={plan}
            price={price}
            amount={amount}
            showEmail={false}
            sent={fallbackSent}
            onSent={() => setFallbackSent(true)}
          />
        ) : null}
      </div>
    )
  }

  if (state.status === "needs-email") {
    return (
      <>
      <CheckoutIntro offer={state.offer} securityLine="none" />
      <RecurringEmailStep
        offer={state.offer}
        amountCents={state.amountCents}
        currency={state.currency}
        recurringInterval={state.recurringInterval}
        prefillEmail={state.prefillEmail}
        submitting={emailSubmitting}
        error={emailError}
        onSubmit={(email) => void submitRecurringEmail(email)}
        deferredStart={state.deferredStart}
        createsAccount={state.createsAccount}
      />
      {emailError && checkoutIsDown(emailErrorStatus) ? (
        <div className="mt-8">
          <CheckoutFallback
            offerTitle={state.offer.title}
            plan={plan}
            price={price}
            amount={amount}
            showEmail={false}
            sent={fallbackSent}
            onSent={() => setFallbackSent(true)}
            prefillEmail={state.prefillEmail}
            recurring
          />
        </div>
      ) : null}
      </>
    )
  }

  return (
    <ReadyCheckout
      // A fresh clientSecret means a fresh PaymentIntent (e.g. a promo code
      // just repriced the order) — remount Elements clean rather than let
      // Stripe.js silently ignore the changed clientSecret prop.
      key={state.clientSecret}
      clientSecret={state.clientSecret}
      paymentIntentId={state.paymentIntentId}
      offer={state.offer}
      amountCents={state.amountCents}
      currency={state.currency}
      prefillEmail={state.prefillEmail}
      paypalReady={paypalReady && !state.guestCheckout}
      recurringInterval={state.recurringInterval}
      intentKind={state.intentKind}
      deferredStart={state.deferredStart}
      promo={
        state.recurringInterval
          ? null
          : {
              onApply: (rawCode: string) => void applyPromoCode(rawCode),
              applying: promoApplying,
              error: promoError,
              applied: promoApplied,
            }
      }
    />
  )
}

export function CheckoutClient({
  plan,
  price,
  amount,
  initialOffer,
  initialAmountCents,
  paypalStandalone = null,
}: {
  plan: string | null
  price: string | null
  amount: string | null
  /** Server-resolved copy for a known ?plan — paints before any network call. */
  initialOffer: OfferCopy | null
  initialAmountCents: number | null
  /** What PayPal alone would charge if Stripe cannot take this payment. */
  paypalStandalone?: PayPalStandaloneOffer | null
}) {
  if (!plan && !price && !amount) {
    return (
      <NoticeShell
        heading="No payment selected"
        body="Open this page with a specific amount or price to start checkout."
      >
        <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
          Need help? Email <EmailLink />.
        </p>
      </NoticeShell>
    )
  }

  if (!stripePromise) {
    return (
      <NoticeShell
        heading="Checkout isn't configured yet"
        body="This site is missing its Stripe publishable key. Set NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY to enable embedded checkout."
      >
        <div className="mt-6 w-full">
          <CheckoutFallback offerTitle={initialOffer?.title ?? null} plan={plan} price={price} amount={amount} />
        </div>
      </NoticeShell>
    )
  }

  return (
    <>
      <section className="tone-paper">
        <div className={cn(SECTION, "py-8 md:py-10")}>
          <div className="mx-auto max-w-[62rem]">
            <EmbeddedCheckoutSession
              key={`${plan}|${price}|${amount}`}
              plan={plan}
              price={price}
              amount={amount}
              initialOffer={initialOffer}
              initialAmountCents={initialAmountCents}
              paypalStandalone={paypalStandalone}
            />
          </div>
        </div>
      </section>
    </>
  )
}
